import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { FormInput, FormSelect } from '../finance';
import { X, CircleNotch } from '@phosphor-icons/react';
import studentsService from '../../services/studentsService';
import transportService from '../../services/transportService';
import './transport-forms.css';

function EnrollmentForm({ enrollment, onSave, onClose }) {
  const [formData, setFormData] = useState(enrollment || {
    student_id: '',
    route_id: '',
    pickup_point: '',
    dropoff_point: '',
    emergency_contact: '',
    emergency_contact_phone: '',
    enrollment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [students, setStudents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingOptions(true);
        const [studentsData, routesData] = await Promise.all([
          studentsService.listStudents({ limit: 100 }).catch(() => []),
          transportService.listRoutes().catch(() => [])
        ]);
        console.log('Fetched students:', studentsData);
        console.log('Fetched routes:', routesData);
        
        // Handle paginated response from students endpoint
        const studentsList = studentsData?.items || studentsData?.students || (Array.isArray(studentsData) ? studentsData : []);
        const routesList = Array.isArray(routesData) ? routesData : routesData?.routes || [];
        
        console.log('Processed students:', studentsList);
        console.log('Processed routes:', routesList);
        
        setStudents(studentsList);
        setRoutes(routesList);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoadingOptions(false);
      }
    };
    loadData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student_id || !formData.route_id) {
      toast.error('Student and route are required');
      return;
    }

    console.log('Submitting enrollment with data:', formData);
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  const studentOptions = students.map(student => ({
    value: student.id,
    label: `${student.first_name} ${student.last_name} (${student.student_id})`
  }));

  const routeOptions = routes.map(route => ({
    value: route.id,
    label: `${route.route_name} (${route.route_code})`
  }));

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>{enrollment ? 'Edit Enrollment' : 'Enroll Student'}</h2>
          <button onClick={onClose} className="btn-close"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-row">
            <FormSelect
              label="Student"
              name="student_id"
              value={formData.student_id}
              onChange={handleChange}
              options={studentOptions}
              placeholder={loadingOptions ? "Loading students..." : "Select Student"}
              disabled={loadingOptions || studentOptions.length === 0}
              required
            />

            <FormSelect
              label="Route"
              name="route_id"
              value={formData.route_id}
              onChange={handleChange}
              options={routeOptions}
              placeholder={loadingOptions ? "Loading routes..." : "Select Route"}
              disabled={loadingOptions || routeOptions.length === 0}
              required
            />
          </div>

          <div className="form-row">
            <FormInput
              label="Pickup Point"
              name="pickup_point"
              value={formData.pickup_point}
              onChange={handleChange}
              placeholder="e.g., Residential address"
            />
            <FormInput
              label="Dropoff Point"
              name="dropoff_point"
              value={formData.dropoff_point}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <FormInput
              label="Emergency Contact Name"
              name="emergency_contact"
              value={formData.emergency_contact}
              onChange={handleChange}
            />
            <FormInput
              label="Emergency Contact Phone"
              name="emergency_contact_phone"
              value={formData.emergency_contact_phone}
              onChange={handleChange}
              type="tel"
            />
          </div>

          <FormInput
            label="Enrollment Date"
            name="enrollment_date"
            type="date"
            value={formData.enrollment_date}
            onChange={handleChange}
          />

          <FormInput
            label="Notes"
            name="notes"
            as="textarea"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
          />

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <span className="button-loading">
                  <CircleNotch size={16} weight="fill" className="spinner" />
                  Saving...
                </span>
              ) : enrollment ? 'Update Enrollment' : 'Enroll Student'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EnrollmentForm;
